import {
  PUTAWAY_REASON_LABELS,
  type ConfirmPutawayResult,
  type InboundCorrectionResult,
  type MixedBinDetails,
  type PutawayDoneView,
  type PutawayTaskView,
  type RedirectedPutaway,
  type UnplannedStock,
  type WarehouseMap,
} from '@wms/domain';
import { useRef, useState } from 'react';
import { api, ApiError, errorMessage, type FloorAction } from '../api';
import { mixedBinOf, mixedBinPrompt } from '../binStock';
import { feedback } from '../feedback';
import { binCode, fmt } from '../format';
import { useSession } from '../session';
import { ErrorBanner, Modal, ScanField, SellerChip, Spinner, useLoad, useToast } from '../ui';
import { Icon } from '../ui/icons';
import { NextWorkStep } from '../ui/NextStep';

/** Where a task's goods come from, as the floor calls it. */
const originLabel = (t: PutawayTaskView) =>
  t.origin === 'cancelled_order'
    ? `Rafa iade · ${t.orderNumber}`
    : t.origin === 'customer_return'
      ? `Müşteri iadesi · ${t.rmaNumber ?? t.orderNumber}`
      : t.asnNumber
        ? `Mal kabul · ${t.asnNumber}`
        : 'Mal kabul';

/** Directed putaway from the dock, return-to-stock of cancelled orders (rafa iade) and sellable customer returns. */
export function Putaway() {
  const { warehouseId, isSupervisor, isAdmin } = useSession();
  // the managers' steps: another bin for a task, a task deleted, a putaway taken back
  const [redirecting, setRedirecting] = useState<PutawayTaskView | null>(null);
  const [deleting, setDeleting] = useState<PutawayTaskView | null>(null);
  const [undoing, setUndoing] = useState<PutawayDoneView | null>(null);
  const done = useLoad(
    () => (isSupervisor && warehouseId ? api<PutawayDoneView[]>('GET', `/api/wms/putaway/done?warehouseId=${warehouseId}`) : Promise.resolve([])),
    [warehouseId, isSupervisor],
    60_000,
  );
  // goods left in the receiving area when their task was deleted
  const unplanned = useLoad(
    () => (warehouseId ? api<UnplannedStock[]>('GET', `/api/wms/putaway/unplanned?warehouseId=${warehouseId}`) : Promise.resolve([])),
    [warehouseId],
    30_000,
  );
  // the recorded volume or load says no, but the goods fit: a supervisor confirms
  const [capacity, setCapacity] = useState<{ task: PutawayTaskView; bin: string; why: string; acceptMixing: boolean } | null>(null);
  const toast = useToast();
  const tasks = useLoad(
    () => (warehouseId ? api<PutawayTaskView[]>('GET', `/api/wms/putaway/tasks?warehouseId=${warehouseId}`) : Promise.resolve([])),
    [warehouseId],
    15_000,
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [override, setOverride] = useState<{ task: PutawayTaskView; bin: string; suggested: string | null } | null>(null);
  // the bin holds another seller's stock (or another product): anyone may put the goods there after the warning
  const [mixed, setMixed] = useState<{ task: PutawayTaskView; bin: string; details: MixedBinDetails } | null>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'bad' | 'warn'; text: string; undoable?: boolean } | null>(null);

  const list = tasks.data ?? [];
  const selected = list.find((t) => t.id === selectedId) ?? list[0] ?? null;
  // a task picked from the list below: on a phone its card (and the scan field) comes into view
  const card = useRef<HTMLElement>(null);
  const pick = (id: string) => {
    setSelectedId(id);
    card.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const confirm = async (
    task: PutawayTaskView,
    bin: string,
    passed: { overrideReason?: string; acceptCapacity?: boolean; acceptMixing?: boolean } = {},
    action?: FloorAction,
  ) => {
    try {
      const result = await api<ConfirmPutawayResult>(
        'POST',
        `/api/wms/putaway/tasks/${task.id}/confirm`,
        {
          locationBarcode: bin,
          ...(passed.overrideReason ? { overrideReason: passed.overrideReason } : {}),
          ...(passed.acceptCapacity ? { acceptCapacity: true } : {}),
          ...(passed.acceptMixing ? { acceptMixing: true } : {}),
        },
        { action },
      );
      setCapacity(null);
      setMixed(null);
      feedback('ok');
      const mixedWords = result.overridden.some((r) => r === 'other_seller' || r === 'other_product') ? ' (karışık göz, onaylandı)' : '';
      setMessage({ tone: 'ok', text: `${result.qty} adet ${task.sku} → ${result.locationCode}${mixedWords}`, undoable: true });
      setOverride(null);
      setSelectedId(null);
      reloadAll();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'OVERRIDE_REASON_REQUIRED') {
        setOverride({ task, bin, suggested: (e.details as { suggested?: string | null } | undefined)?.suggested ?? null });
        return;
      }
      // "Bu gözde başka satıcının ürünü var": a warning to confirm with one press, never a refusal
      const details = e instanceof ApiError ? mixedBinOf(e.code, e.details) : null;
      if (details) {
        feedback('warn');
        setMixed({ task, bin, details });
        return;
      }
      const rejection = e instanceof ApiError ? (e.details as { rejection?: string } | undefined)?.rejection : undefined;
      if (rejection === 'volume_exceeded' || rejection === 'weight_exceeded') {
        feedback('error');
        if (isSupervisor) {
          setCapacity({ task, bin, why: errorMessage(e), acceptMixing: passed.acceptMixing ?? false });
          return;
        }
        setMessage({ tone: 'bad', text: `${errorMessage(e)}. Ürün sığdıysa depo yöneticisi ya da vardiya sorumlusu onaylayarak buraya koyabilir.` });
        return;
      }
      feedback('error');
      setMessage({ tone: 'bad', text: errorMessage(e) });
    }
  };
  const mixedPrompt = mixed ? mixedBinPrompt(mixed.details, { verb: 'koy', supervisor: isSupervisor }) : null;
  // a dialog's confirmation ("Yine de … gözüne koy", "Sığdı", a reason) is sent once: a second tap on a
  // slow network would find the task closed and report an error after the placement counted
  const confirming = useRef(false);
  const [busy, setBusy] = useState(false);
  const confirmOnce = async (task: PutawayTaskView, bin: string, passed: { overrideReason?: string; acceptCapacity?: boolean; acceptMixing?: boolean }) => {
    if (confirming.current) return;
    confirming.current = true;
    setBusy(true);
    try {
      await confirm(task, bin, passed);
    } finally {
      confirming.current = false;
      setBusy(false);
    }
  };

  // geri al: my latest confirmation (last hour) — the units return to where the task started
  const undo = async () => {
    try {
      const task = await api<PutawayTaskView>('POST', '/api/wms/putaway/undo-last');
      feedback('ok');
      setMessage({ tone: 'warn', text: `Yerleştirme geri alındı: ${task.qty} adet ${task.sku} yeniden ${task.fromLocationCode} alanında, görev açıldı.` });
      setSelectedId(task.id);
      reloadAll();
    } catch (e) {
      feedback('error');
      setMessage({ tone: 'bad', text: errorMessage(e) });
    }
  };

  const replan = async (task: PutawayTaskView) => {
    try {
      await api('POST', `/api/wms/putaway/tasks/${task.id}/replan`);
      void tasks.reload();
      toast('Öneriler güncellendi', 'ok');
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };

  // after a manager's step every list may have changed
  const reloadAll = () => {
    void tasks.reload();
    void done.reload();
    void unplanned.reload();
  };
  const said = (tone: 'ok' | 'warn' | 'bad', text: string) => {
    feedback(tone === 'bad' ? 'error' : 'ok');
    setMessage({ tone, text });
  };

  const plan = async (u: UnplannedStock) => {
    try {
      const made = await api<PutawayTaskView[]>('POST', '/api/wms/putaway/unplanned', {
        warehouseId,
        locationId: u.locationId,
        productId: u.productId,
        lotNo: u.lotNo,
        expiryDate: u.expiryDate,
      });
      said('ok', `${u.qty} adet ${u.sku} için yerleştirme görevi oluşturuldu${made[0]?.suggestions[0] ? ` · önerilen göz ${made[0].suggestions[0].code}` : ''}.`);
      if (made[0]) setSelectedId(made[0].id);
      reloadAll();
    } catch (e) {
      said('bad', errorMessage(e));
    }
  };

  if (tasks.error) return <ErrorBanner message={tasks.error} onRetry={() => void tasks.reload()} />;
  if (!tasks.data) return <Spinner />;

  return (
    <div className="split putaway">
      <div className="stack">
        {message && (
          <div className={`banner ${message.tone}`}>
            <div style={{ flex: 1 }}>{message.text}</div>
            {message.undoable && (
              <button className="btn sm" onClick={() => void undo()}>
                <Icon name="undo" size={16} /> Geri al
              </button>
            )}
          </div>
        )}
        {selected ? (
          <section className="card putaway-card" ref={card}>
            <div className="card-b stack">
              {/* on top: on a phone the field is in reach before the product and the bin are read */}
              <ScanField
                placeholder={isSupervisor ? 'Gözün etiketini okutun ya da kodunu yazın' : 'Yerleştirdiğiniz gözün etiketini okutun'}
                onScan={(bin, action) => confirm(selected, bin, {}, action)}
                // a manager typing a bin code is offered the bins it may mean (with their fill and sellers)
                suggest={{ kinds: ['bin'] }}
                disabled={override !== null || capacity !== null || mixed !== null}
              />
              <div className="row">
                <span className={`chip ${selected.origin === 'inbound' ? 'info' : 'warn'}`}>{originLabel(selected)}</span>
                <SellerChip code={selected.tenantCode} />
                <span className="mono muted">{selected.sku}</span>
              </div>
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'nowrap' }}>
                <div style={{ minWidth: 0 }}>
                  <div className="putaway-name">{selected.productName}</div>
                  <div className="muted">
                    {selected.fromLocationCode} alanından
                    {selected.lotNo && ` · Lot ${selected.lotNo}`}
                    {selected.expiryDate && ` · SKT ${fmt.date(selected.expiryDate)}`}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flex: 'none' }}>
                  <div className="qty-big">{selected.qty}</div>
                  <div className="muted">adet</div>
                </div>
              </div>
              {selected.suggestions[0] ? (
                <div>
                  <div className="muted" style={{ fontWeight: 600 }}>
                    Önerilen göz
                  </div>
                  <div className="loc-big">{selected.suggestions[0].code}</div>
                  <div className="row" style={{ marginTop: 8 }}>
                    {selected.suggestions[0].reasons.map((r) => (
                      <span key={r} className="chip ok">
                        {PUTAWAY_REASON_LABELS[r]}
                      </span>
                    ))}
                  </div>
                  {isSupervisor ? (
                    <div className="row" style={{ marginTop: 10 }}>
                      {selected.suggestions.map((s, i) => (
                        <button key={s.code} className={`btn${i === 0 ? ' primary' : ''}`} onClick={() => void confirm(selected, s.code)}>
                          {s.code} gözüne yerleştir
                        </button>
                      ))}
                    </div>
                  ) : (
                    selected.suggestions.length > 1 && (
                      <div className="muted" style={{ marginTop: 8, fontSize: 13 }}>
                        Alternatif: {selected.suggestions.slice(1).map((s) => s.code).join(', ')}
                      </div>
                    )
                  )}
                </div>
              ) : (
                <div className="banner warn">Uygun göz bulunamadı. Yeniden önerin veya bir göz okutup gerekçe girin.</div>
              )}
              <div className="row">
                {/* a bin a manager chose stays: the staff put the goods there, or elsewhere with a reason */}
                {(isSupervisor || !selected.suggestions[0]?.reasons.includes('chosen_by_manager')) && (
                  <button className="btn" onClick={() => void replan(selected)}>
                    <Icon name="refresh" /> Yeniden öner
                  </button>
                )}
                <button className="btn" onClick={() => void undo()} title="Son bir saatte onayladığınız son yerleştirmeyi geri alır">
                  <Icon name="undo" /> Son yerleştirmeyi geri al
                </button>
              </div>
              {isSupervisor && (
                <div className="putaway-manage">
                  <button className="btn" onClick={() => setRedirecting(selected)} title="Ürünü başka bir göze yönlendirin; depo elemanı oraya koyar">
                    <Icon name="move" /> Hedef gözü değiştir
                  </button>
                  {selected.origin === 'inbound' && (
                    <button className="btn" onClick={() => setDeleting(selected)} title="Görevi silin ya da gelmeyen ürünü kabulden düşün">
                      <Icon name="trash" /> Görevi sil
                    </button>
                  )}
                  {selected.asnNumber && (
                    <a className="btn ghost" href={`#/kabul/${selected.asnNumber}`} title="Gönderinin kabul ekranı: adetleri orada düzeltin">
                      <Icon name="inbox" /> {selected.asnNumber}
                    </a>
                  )}
                </div>
              )}
            </div>
          </section>
        ) : (
          <NextWorkStep
            from="/yerlestirme"
            tone="ok"
            title="Yerleştirilecek ürün kalmadı"
            steps={['Mal kabulde okutulan ürünler, iptal edilen siparişlerden ve müşteri iadelerinden dönen sağlam ürünler burada belirir.']}
          >
            <div className="row">
              <button className="btn" onClick={() => void undo()}>
                <Icon name="undo" /> Son yerleştirmeyi geri al
              </button>
            </div>
          </NextWorkStep>
        )}
        {(unplanned.data ?? []).length > 0 && (
          <section className="card">
            <div className="card-h">
              <h2>Kabul alanında görevsiz ürün</h2>
              <span className="sub">Görevi silinen ürünler: rafa konacaksa görev oluşturun</span>
            </div>
            <div className="table-wrap">
              <table className="t stack-sm">
                <tbody>
                  {(unplanned.data ?? []).map((u) => (
                    <tr key={`${u.locationId}|${u.productId}|${u.lotNo ?? ''}|${u.expiryDate ?? ''}`}>
                      <td>
                        <b>{u.productName}</b>
                        <div className="muted" style={{ fontSize: 12 }}>
                          <SellerChip code={u.tenantCode} /> <span className="mono">{u.sku}</span>
                          {u.lotNo && ` · Lot ${u.lotNo}`}
                          {u.expiryDate && ` · SKT ${fmt.date(u.expiryDate)}`}
                        </div>
                      </td>
                      <td className="num" data-label="Adet">
                        {fmt.n(u.qty)}
                      </td>
                      <td className="mono" data-label="Alan">
                        {u.locationCode}
                      </td>
                      <td>
                        {isSupervisor ? (
                          <button className="btn sm" onClick={() => void plan(u)}>
                            <Icon name="plus" size={16} /> Görev oluştur
                          </button>
                        ) : (
                          <span className="muted">Sorumluya haber verin</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
      <div className="stack">
        <section className="card">
          <div className="card-h">
            <h2>Görevler</h2>
            <span className="sub">{list.length} açık görev</span>
          </div>
          <div className="table-wrap">
            {/* a phone: the product on its own line, the count and the bin named under it */}
            <table className="t stack-sm putaway-rows">
              <tbody>
                {list.map((t) => (
                  <tr key={t.id} className={`clickable${t.id === selected?.id ? ' selected' : ''}`} onClick={() => pick(t.id)}>
                    <td className="putaway-what">
                      <b>{t.productName}</b>
                      <div className="muted" style={{ fontSize: 12 }}>
                        {originLabel(t)} · {t.fromLocationCode}
                      </div>
                    </td>
                    <td className="num" data-label="Adet">
                      {t.qty}
                    </td>
                    <td className="mono" data-label="Göz">
                      {t.suggestions[0]?.code ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        {isSupervisor && <DoneList items={done.data ?? []} canUndo={isAdmin} onUndo={setUndoing} />}
      </div>
      {redirecting && warehouseId && (
        <RedirectDialog
          task={redirecting}
          warehouseId={warehouseId}
          onClose={() => setRedirecting(null)}
          onDone={(task) => {
            setRedirecting(null);
            setSelectedId(task.id);
            said(task.warning ? 'warn' : 'ok', `${task.sku} artık ${task.suggestions[0]?.code ?? 'seçilen'} gözüne yerleştirilecek.${task.warning ? ` ${task.warning}` : ''}`);
            reloadAll();
          }}
        />
      )}
      {deleting && (
        <DeleteTaskDialog
          task={deleting}
          onClose={() => setDeleting(null)}
          onDone={(text) => {
            setDeleting(null);
            setSelectedId(null);
            said('ok', text);
            reloadAll();
          }}
        />
      )}
      {undoing && (
        <UndoPutawayDialog
          task={undoing}
          onClose={() => setUndoing(null)}
          onDone={(task) => {
            setUndoing(null);
            setSelectedId(task.id);
            said('warn', `Yerleştirme geri alındı: ${task.qty} adet ${task.sku} yeniden ${task.fromLocationCode} alanında, görev açıldı.`);
            reloadAll();
          }}
        />
      )}
      {capacity && (
        <Modal
          title={`${binCode(capacity.bin)} · ${capacity.why.toLocaleLowerCase('tr')}`}
          onClose={() => setCapacity(null)}
          footer={
            <>
              <button className="btn" onClick={() => setCapacity(null)}>
                Vazgeç
              </button>
              <button
                className="btn primary"
                disabled={busy}
                onClick={() => void confirmOnce(capacity.task, capacity.bin, { acceptCapacity: true, acceptMixing: capacity.acceptMixing })}
              >
                Sığdı, {binCode(capacity.bin)} gözüne yerleştir
              </button>
            </>
          }
        >
          <div className="stack">
            <p style={{ margin: 0 }}>
              Kayıtlı ölçülere göre {capacity.task.qty} adet {capacity.task.sku} bu göze sığmıyor görünüyor. Ürünler gerçekten sığdıysa onaylayın;
              yerleştirme kaydına “sorumlu onayladı” diye yazılır.
            </p>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              Bu uyarı sık çıkıyorsa gözün hacmi ya da taşıma kapasitesi Depo tanımında küçük girilmiş, ya da ürünün ölçüsü büyük ölçülmüş olabilir.
            </p>
          </div>
        </Modal>
      )}
      {mixed && mixedPrompt && (
        <Modal
          title={mixedPrompt.title}
          onClose={() => setMixed(null)}
          footer={
            <>
              <button className="btn" onClick={() => setMixed(null)}>
                {mixedPrompt.confirm ? 'Vazgeç' : 'Kapat'}
              </button>
              {/* a press, never typing: a staff member with a reader in hand confirms here too */}
              {mixedPrompt.confirm && (
                <button
                  className="btn primary"
                  disabled={busy}
                  onClick={() => void confirmOnce(mixed.task, mixed.bin, { acceptMixing: true, ...(mixedPrompt.acceptCapacity ? { acceptCapacity: true } : {}) })}
                >
                  {mixedPrompt.confirm}
                </button>
              )}
            </>
          }
        >
          <div className="stack">
            {mixedPrompt.lines.map((line, i) => (
              <p key={i} style={{ margin: 0 }}>
                {i === mixedPrompt.lines.length - 1 && mixedPrompt.confirm ? <b>{line}</b> : line}
              </p>
            ))}
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              Onaylarsanız yerleştirme kaydına kimin onayladığı ve “karışık göz” yazılır. Toplama, sayım ve stok her satıcı için ayrı tutulur.
            </p>
          </div>
        </Modal>
      )}
      {override && (
        <OverrideDialog
          bin={override.bin}
          suggested={override.suggested}
          onClose={() => setOverride(null)}
          busy={busy}
          onConfirm={(reason) => void confirmOnce(override.task, override.bin, { overrideReason: reason })}
        />
      )}
    </div>
  );
}

/** why the goods went elsewhere; on pallets, different goods share a pallet that still has room */
const OVERRIDE_REASONS = [
  'Palette yer vardı (karışık palet)',
  'Aynı ürünün yanına',
  'Daha yakın / daha uygun göz',
  'Önerilen göz dolu',
  'Önerilen göz hasarlı / kapalı',
  'Ürün göze sığmadı',
  'Sorumlu talimatı',
];
const OTHER_REASON = 'Diğer…';

function OverrideDialog(props: { bin: string; suggested: string | null; busy: boolean; onClose: () => void; onConfirm: (reason: string) => void }) {
  // one of the usual reasons, or the dock's own words
  const [reason, setReason] = useState<string | null>(null);
  const [own, setOwn] = useState('');
  const said = reason === OTHER_REASON ? own.trim() : reason;
  return (
    <Modal
      title="Önerilenden farklı göz"
      onClose={props.onClose}
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={!said || props.busy} onClick={() => said && props.onConfirm(said)}>
            {binCode(props.bin)} gözüne yerleştir
          </button>
        </>
      }
    >
      <div className="stack">
        <p style={{ margin: 0 }}>
          Önerilen göz {props.suggested ? <b>{props.suggested}</b> : 'başka'} idi. Neden <b>{binCode(props.bin)}</b>?
        </p>
        <div className="row">
          {[...OVERRIDE_REASONS, OTHER_REASON].map((r) => (
            <button key={r} className={`chip select${reason === r ? ' on' : ''}`} onClick={() => setReason(r)}>
              {r}
            </button>
          ))}
        </div>
        {reason === OTHER_REASON && (
          <div className="field">
            <label htmlFor="override-own">Gerekçe</label>
            <input
              id="override-own"
              className="input"
              autoFocus
              maxLength={200}
              value={own}
              onChange={(e) => setOwn(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && own.trim()) props.onConfirm(own.trim());
              }}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}

/**
 * The putaways of the last two weeks, newest first: where each went, who put it there. The depo
 * yöneticisi takes one back (the units return to where the task started and it opens again); the
 * map moves what is already on a shelf.
 */
function DoneList(props: { items: readonly PutawayDoneView[]; canUndo: boolean; onUndo: (task: PutawayDoneView) => void }) {
  const [all, setAll] = useState(false);
  if (props.items.length === 0) return null;
  const shown = all ? props.items : props.items.slice(0, 8);
  return (
    <section className="card">
      <div className="card-h">
        <h2>Son yerleştirmeler</h2>
        <span className="sub">{props.canUndo ? 'Yanlış göze konanı geri alın ya da haritadan taşıyın' : 'Geri almayı depo yöneticisi yapar'}</span>
      </div>
      <div className="table-wrap">
        <table className="t stack-sm putaway-rows">
          <tbody>
            {shown.map((t) => (
              <tr key={t.id}>
                <td className="putaway-what">
                  <b>{t.productName}</b>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {originLabel(t)} · {fmt.dateTime(t.completedAt)}
                    {t.completedBy && ` · ${t.completedBy}`}
                  </div>
                </td>
                <td className="num" data-label="Adet">
                  {fmt.n(t.qty)}
                </td>
                <td className="mono" data-label="Göz">
                  {t.toLocationCode}
                </td>
                {props.canUndo && (
                  <td>
                    <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                      <button className="btn sm" onClick={() => props.onUndo(t)} title="Ürünü gözden alıp kabul alanına geri koyar, görev yeniden açılır">
                        <Icon name="undo" size={16} /> Geri al
                      </button>
                      <a className="btn sm ghost" href={`#/harita?q=${encodeURIComponent(t.sku)}`} title="Depo haritasında başka bir göze taşıyın">
                        <Icon name="map" size={16} /> Taşı
                      </a>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {props.items.length > shown.length && (
        <div className="card-b">
          <button className="btn sm ghost" onClick={() => setAll(true)}>
            Tümünü göster ({props.items.length})
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * "Hedef gözü değiştir": the manager sends an open task to another bin; the floor then puts it there
 * as the suggestion (no reason asked). The codes come up while typing; the engine's own bins are a tap.
 */
function RedirectDialog(props: { task: PutawayTaskView; warehouseId: string; onClose: () => void; onDone: (task: RedirectedPutaway) => void }) {
  const { task } = props;
  const [bin, setBin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const map = useLoad(() => api<WarehouseMap>('GET', `/api/wms/map?warehouseId=${props.warehouseId}`), [props.warehouseId]);
  const codes = (map.data?.bins ?? []).filter((b) => (b.type === 'pick' || b.type === 'bulk') && !b.isBlocked).map((b) => b.code);
  const send = async (code: string) => {
    if (!code.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      props.onDone(await api<RedirectedPutaway>('POST', `/api/wms/putaway/tasks/${task.id}/destination`, { locationBarcode: code.trim() }));
    } catch (e) {
      feedback('error');
      setError(errorMessage(e));
      setBusy(false);
    }
  };
  return (
    <Modal
      title={`${task.sku} · hedef göz`}
      onClose={props.onClose}
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={!bin.trim() || busy} onClick={() => void send(bin)}>
            {bin.trim() ? `${binCode(bin.trim().toUpperCase())} gözüne yönlendir` : 'Önce gözü seçin'}
          </button>
        </>
      }
    >
      <div className="stack rcv-dialog">
        {error && <div className="banner bad">{error}</div>}
        <p style={{ margin: 0 }}>
          {task.qty} adet <b>{task.productName}</b> şimdi {task.suggestions[0] ? <b>{task.suggestions[0].code}</b> : 'önerisiz'} gözüne gidiyor. Depo elemanı ürünü
          sizin seçtiğiniz göze koyar; ekranında “Yönetici bu gözü seçti” yazar.
        </p>
        <div className="field">
          <label htmlFor="redirect-bin">Göz kodu ya da etiketi</label>
          <input
            id="redirect-bin"
            className="input mono"
            list="redirect-bins"
            autoFocus
            autoComplete="off"
            placeholder="ör. A-01-2-1"
            value={bin}
            onChange={(e) => setBin(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void send(bin);
            }}
          />
          <datalist id="redirect-bins">
            {codes.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
          <span className="hint">Yazmaya başlayınca gözler listelenir; etiketi okutabilirsiniz de.</span>
        </div>
        {task.suggestions.length > 0 && (
          <div className="row">
            <span className="muted">Önerilenler:</span>
            {task.suggestions.map((s) => (
              <button key={s.code} className={`chip select${bin.trim().toUpperCase() === s.code ? ' on' : ''}`} onClick={() => setBin(s.code)}>
                {s.code}
              </button>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

/**
 * "Görevi sil" for goods from the dock: either they never came (taken off stock and off the receipt
 * that counted them) or the task alone goes and the goods wait in the receiving area, listed as
 * görevsiz until a task is made for them again.
 */
function DeleteTaskDialog(props: { task: PutawayTaskView; onClose: () => void; onDone: (text: string) => void }) {
  const { task } = props;
  const fromReceipt = task.asnNumber !== null;
  const [choice, setChoice] = useState<'missing' | 'keep'>(fromReceipt ? 'missing' : 'keep');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      if (choice === 'missing') {
        const result = await api<InboundCorrectionResult>('POST', `/api/wms/putaway/tasks/${task.id}/remove-units`, {});
        props.onDone(`${task.qty} adet ${task.sku} stoktan çıkarıldı; ${task.asnNumber} kabulünde artık ${result.correction.after.received} sağlam.`);
      } else {
        await api('POST', `/api/wms/putaway/tasks/${task.id}/cancel`);
        props.onDone(`Görev silindi: ${task.qty} adet ${task.sku} ${task.fromLocationCode} alanında görevsiz bekliyor.`);
      }
    } catch (e) {
      feedback('error');
      setError(errorMessage(e));
      setBusy(false);
    }
  };
  return (
    <Modal
      title={`${task.sku} · görevi sil`}
      onClose={props.onClose}
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          <button className="btn danger" disabled={busy} onClick={() => void run()}>
            <Icon name="trash" /> {choice === 'missing' ? 'Kabulden düş' : 'Görevi sil'}
          </button>
        </>
      }
    >
      <div className="stack rcv-dialog">
        {error && <div className="banner bad">{error}</div>}
        <p style={{ margin: 0 }}>
          {task.qty} adet <b>{task.productName}</b> {task.fromLocationCode} alanında yerleştirme bekliyor.
        </p>
        <div className="choice-list">
          {fromReceipt && (
            <label className={`choice${choice === 'missing' ? ' on' : ''}`}>
              <input type="radio" name="del-kind" checked={choice === 'missing'} onChange={() => setChoice('missing')} />
              <span>
                <b>Ürün gelmedi ya da fazla sayıldı: kabulden düş</b>
                <small>
                  {task.qty} adet stoktan çıkarılır, {task.asnNumber} kabulündeki adet {task.qty} azalır. Kapatılmış kabulde yalnız depo yöneticisi yapabilir; satıcıya
                  düzeltilmiş sonuç bildirilir.
                </small>
              </span>
            </label>
          )}
          <label className={`choice${choice === 'keep' ? ' on' : ''}`}>
            <input type="radio" name="del-kind" checked={choice === 'keep'} onChange={() => setChoice('keep')} />
            <span>
              <b>Yalnız görevi sil, ürün kabul alanında kalsın</b>
              <small>Stok değişmez. Ürün “Kabul alanında görevsiz ürün” listesinde bekler; rafa konacaksa oradan yeniden görev oluşturun.</small>
            </span>
          </label>
        </div>
      </div>
    </Modal>
  );
}

/** The depo yöneticisi takes back a putaway anyone made: the units leave the bin for where the task started, the task opens again. */
function UndoPutawayDialog(props: { task: PutawayDoneView; onClose: () => void; onDone: (task: PutawayTaskView) => void }) {
  const { task } = props;
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      props.onDone(await api<PutawayTaskView>('POST', `/api/wms/putaway/tasks/${task.id}/undo`));
    } catch (e) {
      feedback('error');
      setError(errorMessage(e));
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Yerleştirme geri alınsın mı?"
      onClose={props.onClose}
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={busy} onClick={() => void run()}>
            <Icon name="undo" /> Geri al
          </button>
        </>
      }
    >
      <div className="stack rcv-dialog">
        {error && <div className="banner bad">{error}</div>}
        <p style={{ margin: 0 }}>
          {task.qty} adet <b>{task.productName}</b> {task.toLocationCode} gözünden {task.fromLocationCode} alanına geri alınır; görev yeniden açılır ve ürün başka bir
          göze yerleştirilebilir.
        </p>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Siparişe ayrılan ürün geri alınamaz. Ürünü yalnız başka göze koymak için Depo haritasındaki “Taşı” daha kısadır.
        </p>
      </div>
    </Modal>
  );
}
